import { useCallback, useEffect, useMemo, useState } from 'react'
import { NavigateFunction } from 'react-router-dom'

import { OktaAuth } from '@okta/okta-auth-js'
import queryString from 'query-string'
import Cookies from 'universal-cookie'

import { UNAUTHENTICATED_REDIRECT_URL } from '@lyrahealth-inc/lyraweb-mobile/src/hooks/useShareSessionStorageAcrossTabs'
import { INVALID, LOCAL_STORAGE_KEY } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  COMMON_MIXPANEL_PAGE,
  COMMON_MIXPANEL_PROPERTIES,
  LOGIN_EVENTS,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import {
  PROVIDER_RESULTS_SURVEY_DATE_LAST_SEEN_KEY,
  SHOW_PROVIDER_RESULTS_SURVEY_KEY,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/pendoConstants'
import { APP_ROUTE, PAGE_ROUTES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/routingConstants'
import { IDENTITY_PROVIDERS } from '@lyrahealth-inc/shared-app-logic/src/features/registration/constants'
import { getShowProviderResultsSurveyUsingDateLastSeen } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/utils'
import { LoginResponse } from '@lyrahealth-inc/shared-app-logic/src/models/login/Login'

import mixpanel from '../../data/mixpanel/mixpanel-browser'
import { makeOktaAuthOptions } from '../../features/login/loginUtils'
import {
  ATTESTATION_VERIFICATION,
  CONNECTED_SERVICE_DATA_COOKIE,
  PARTNER_SSO_REGISTRATION_DATA_COOKIE,
  PARTNER_SSO_REGISTRATION_PARTNER_IDP_QUERY_PARAM,
  PARTNER_SSO_REGISTRATION_VERIFICATION_KEY_QUERY_PARAM,
} from '../constants/appConstants'
import { GENERIC, REGISTERED } from '../constants/errorConstants'
import { BASIC_INFO_FORM_PATH } from '../constants/registrationFormConstants'
import { getParentDomain } from '../utils/cookieUtils'
import { addFromUrlRedirectParam } from '../utils/routingUtils'

const CARE_TOOL_CN_LOGIN_PATH = '/cn-login'

function isCareToolCnLoginPath(path: string): boolean {
  try {
    const parsedUrl = new URL(path, window.location.origin)
    return (
      parsedUrl.origin === window.location.origin &&
      (parsedUrl.pathname === CARE_TOOL_CN_LOGIN_PATH || parsedUrl.pathname.startsWith(`${CARE_TOOL_CN_LOGIN_PATH}/`))
    )
  } catch {
    return false
  }
}

export type UseAuthenticateProps = {
  login: Function // eslint-disable-line @typescript-eslint/no-unsafe-function-type
  trackEventWithObj: Function // eslint-disable-line @typescript-eslint/no-unsafe-function-type
  markUserAsStale: Function // eslint-disable-line @typescript-eslint/no-unsafe-function-type
  mixpanelInitialized: boolean
  getOktaSession?: () => Promise<{ idp?: { id: string; type: string } }>
  navigate: NavigateFunction
  oktaClientId: string
  oktaBaseUrl: string
  oktaAppleSSOClientId: string
  oktaGoogleSSOClientId: string
  setSsoIdentityProvider: Function // eslint-disable-line @typescript-eslint/no-unsafe-function-type
  setSsoToken: Function // eslint-disable-line @typescript-eslint/no-unsafe-function-type
  userId?: string
  customerId?: string
}
const useAuthenticate = ({
  login,
  trackEventWithObj,
  markUserAsStale,
  mixpanelInitialized,
  getOktaSession,
  navigate,
  oktaClientId,
  oktaBaseUrl,
  oktaAppleSSOClientId,
  oktaGoogleSSOClientId,
  setSsoIdentityProvider,
  setSsoToken,
  userId,
  customerId,
}: UseAuthenticateProps) => {
  const { code, state, error, error_description, fromLogin } = queryString.parse(window.location.search) || {}

  // For SSO with partner identity providers:
  // Okta sets a `fromLogin` query parameter in the redirect after authenticating a user from a federated identity provider
  const isLoginFromPartnerSSO = fromLogin === 'true'

  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(
    (Boolean(code) && Boolean(state)) || isLoginFromPartnerSSO,
  )
  const [loginEvent, setLoginEvent] = useState<LOGIN_EVENTS>()
  const [loginEventProps, setLoginEventProps] = useState({})
  const [hasTrackedLogin, setHasTrackedLogin] = useState(false)
  const doLogin = useCallback(async () => {
    try {
      const loginResponse: LoginResponse = await login({ code, redirect_uri: window.location.origin + '/login/okta' })
      markUserAsStale()
      setIsAuthenticating(false)
      if (getOktaSession) {
        getOktaSession()
          .then((session) => {
            if (session.idp?.type === 'SOCIAL') {
              if (session.idp?.id === oktaAppleSSOClientId) {
                setLoginEvent(LOGIN_EVENTS.LOGIN_APPLE_SSO)
              } else if (session.idp?.id === oktaGoogleSSOClientId) {
                setLoginEvent(LOGIN_EVENTS.LOGIN_GOOGLE_SSO)
              }
            } else if (session.idp?.type === 'FEDERATION') {
              setLoginEvent(LOGIN_EVENTS.LOGIN_PARTNER_SSO)
              if (loginResponse.partnerIdp?.idpId === session.idp?.id) {
                setLoginEventProps({ [COMMON_MIXPANEL_PROPERTIES.PARTNER_NAME]: loginResponse.partnerIdp.shortName })
              } else {
                console.error('Unexpected: Partner IDP mismatch between Okta session and login response!')
              }
            } else {
              setLoginEvent(LOGIN_EVENTS.LOGIN)
            }
          })
          .catch((error) => {
            console.warn('Failed to get Okta session', error)
            setLoginEvent(LOGIN_EVENTS.LOGIN)
            if (loginResponse.ssoUserInfo && loginResponse.partnerIdp) {
              // This is the case where an unregistered user came from partner sso.
              // If the user completes registration and logs in, we want to attribute it as a successful partner sso login in mixpanel metrics.
              setLoginEvent(LOGIN_EVENTS.LOGIN_PARTNER_SSO)
              setLoginEventProps({ [COMMON_MIXPANEL_PROPERTIES.PARTNER_NAME]: loginResponse.partnerIdp.shortName })
            }
          })
      } else {
        setLoginEvent(LOGIN_EVENTS.LOGIN)
      }
      // This indicates whether we need to redirect the user to the provided URL.
      // This should only be true when user is logging in from Elevance SSO in which case they would be on
      // care.lyrahealth.com and would need to be redirected to the customer's microsite.
      const needsRedirect = Boolean(loginResponse.loginRedirect?.redirectUrl)

      if (loginResponse.ssoUserInfo) {
        // SSO RECONCILIATION: Existing user is taken to verification page to receive verification email
        if (loginResponse.ssoUserInfo.isAccountReconciliation) {
          const reconciliationPath = '/register/verify'
          const reconciliationParamsString = queryString.stringify({
            isSSOReconciliation: 'true',
            ssoDataKey: loginResponse.ssoUserInfo.ssoReconciliationDataKey,
          })
          if (needsRedirect) {
            const reconciliationURL = new URL(reconciliationPath, loginResponse.loginRedirect!.redirectUrl)
            reconciliationURL.search = reconciliationParamsString
            window.location.replace(reconciliationURL.toString())
          } else {
            navigate({
              pathname: reconciliationPath,
              search: `?${reconciliationParamsString}`,
            })
          }
        } else if (loginResponse.ssoUserInfo.isUnidentifiableUser && needsRedirect) {
          // UNIDENTIFIABLE: User is coming from partner SSO but an email wasn't provided by the Partner IDP, making user unidentifiable with
          // no email available for registration. Currently, this only happens for Elevance which always needs a redirect so we only handle the needsRedirect case.
          window.location.replace(loginResponse.loginRedirect!.redirectUrl)
        } else {
          // UNREGISTERED: User is coming from partner SSO and needs to be taken to registration page
          const registrationParams = {
            isSSO: 'true',
            id: loginResponse.ssoUserInfo.email,
          }
          if (loginResponse.ssoUserInfo.ssoRegistrationVerificationKey) {
            registrationParams[PARTNER_SSO_REGISTRATION_VERIFICATION_KEY_QUERY_PARAM] =
              loginResponse.ssoUserInfo.ssoRegistrationVerificationKey
          }
          if (needsRedirect) {
            if (loginResponse.partnerIdp?.shortName) {
              registrationParams[PARTNER_SSO_REGISTRATION_PARTNER_IDP_QUERY_PARAM] = loginResponse.partnerIdp.shortName
            }
            const parentDomain = getParentDomain()
            const cookies = new Cookies()
            const REGISTRATION_COOKIES_MAX_AGE_SECONDS = 60 * 15 // 15 minutes
            if (
              loginResponse.ssoUserInfo?.givenName ||
              loginResponse.ssoUserInfo?.familyName ||
              loginResponse.connectedServiceLoginData
            ) {
              const cookieData = {
                firstName: loginResponse.ssoUserInfo.givenName || '',
                lastName: loginResponse.ssoUserInfo.familyName || '',
                ...(loginResponse.connectedServiceLoginData?.externalId && {
                  externalUserId: loginResponse.connectedServiceLoginData.externalId,
                }),
                ...(loginResponse.connectedServiceLoginData?.service && {
                  connectedServiceType: loginResponse.connectedServiceLoginData.service,
                }),
              }
              cookies.set(PARTNER_SSO_REGISTRATION_DATA_COOKIE, JSON.stringify(cookieData), {
                path: '/',
                domain: parentDomain,
                maxAge: REGISTRATION_COOKIES_MAX_AGE_SECONDS,
                secure: true,
                sameSite: 'lax',
              })
            }

            const registrationURL = new URL(BASIC_INFO_FORM_PATH, loginResponse.loginRedirect!.redirectUrl)
            registrationURL.search = queryString.stringify(registrationParams)
            window.location.replace(registrationURL.toString())
          } else {
            setSsoIdentityProvider(IDENTITY_PROVIDERS.PARTNER)
            setSsoToken('')
            navigate(
              {
                pathname: BASIC_INFO_FORM_PATH,
                search: `?${queryString.stringify(registrationParams)}`,
              },
              { state: loginResponse },
            )
          }
        }
      } else if (loginResponse.eligibleCustomerForTransferAccount && customerId) {
        window.localStorage.setItem(
          LOCAL_STORAGE_KEY.TRANSFER_ACCOUNT,
          loginResponse.eligibleCustomerForTransferAccount,
        )
        navigate(APP_ROUTE.TRANSFER_ACCOUNT_CONFIRMATION)
      } else if (loginResponse.connectedService) {
        const cookies = new Cookies()
        cookies.remove(CONNECTED_SERVICE_DATA_COOKIE)
        const attestationUrl = `${PAGE_ROUTES.CONNECTED_SERVICE_ATTESTATION}?connectedService=${loginResponse.connectedService}`
        navigate(attestationUrl)
      } else if (loginResponse.needsAttestation) {
        // ATTESTATION: Registered user needs to verify their attestation
        const attestationPath = `/verify-attestation?token=${loginResponse.needsAttestation.token}&id=${loginResponse.needsAttestation.id}&showEligibility=${loginResponse.needsAttestation.showEligibility}`
        if (needsRedirect) {
          const attestationURL = new URL(loginResponse.loginRedirect!.redirectUrl)
          attestationURL.searchParams.set('redirect', attestationPath)
          window.location.replace(attestationURL.toString())
        } else {
          navigate(attestationPath)
        }
      } else {
        // REGISTERED: Registered user needs to be logged in
        // This is the page user was trying to see but had to log in for.
        let attemptedPath = window.sessionStorage.getItem(UNAUTHENTICATED_REDIRECT_URL)
        if (attemptedPath) {
          attemptedPath = addFromUrlRedirectParam(attemptedPath)
        }
        if (needsRedirect) {
          const loginRedirectPath = attemptedPath ?? '/secure/index'
          const loginURL = new URL(loginResponse.loginRedirect!.redirectUrl)
          loginURL.searchParams.set('redirect', loginRedirectPath)
          window.location.replace(loginURL.toString())
        } else {
          window.localStorage.removeItem(LOCAL_STORAGE_KEY.TRANSFER_ACCOUNT)
          window.localStorage.removeItem(ATTESTATION_VERIFICATION)
          if (attemptedPath) {
            window.sessionStorage.removeItem(UNAUTHENTICATED_REDIRECT_URL)
            if (isCareToolCnLoginPath(attemptedPath)) {
              window.location.replace(attemptedPath)
            } else {
              navigate(attemptedPath, { replace: true })
            }
          } else {
            navigate('/secure/index')
          }
        }
      }
    } catch {
      setIsAuthenticating(false)
      navigate('/login')
    }
  }, [
    login,
    code,
    markUserAsStale,
    getOktaSession,
    customerId,
    oktaAppleSSOClientId,
    oktaGoogleSSOClientId,
    navigate,
    setSsoIdentityProvider,
    setSsoToken,
  ])

  useEffect(() => {
    const confirmationEmailErrors = [REGISTERED, INVALID, GENERIC]
    const queryParams = queryString.stringify({ error, error_description })
    if (mixpanelInitialized) {
      if (error) {
        trackEventWithObj({
          event: LOGIN_EVENTS.LOGIN_FAILED,
        })
      }
      if (error && typeof error === 'string' && !confirmationEmailErrors.includes(error)) {
        navigate(`/login?${queryParams}`)
      } else if (error && typeof error === 'string' && error === REGISTERED) {
        navigate(`/register/verify?${queryParams}`)
      }
    }
  }, [error, error_description, navigate, trackEventWithObj, mixpanelInitialized])

  const oktaAuth = useMemo(() => {
    if (oktaBaseUrl && oktaClientId) {
      return new OktaAuth(makeOktaAuthOptions(oktaClientId, oktaBaseUrl, window.location.origin))
    }
    return undefined
  }, [oktaBaseUrl, oktaClientId])

  useEffect(() => {
    if (isLoginFromPartnerSSO && oktaAuth) {
      // The user should have a session with Okta after authenticating with the federated identity provider
      // This starts the OIDC authentication code flow to obtain tokens and log in with LyraWeb
      oktaAuth.token.getWithRedirect()
    } else if (isAuthenticating && mixpanelInitialized) {
      doLogin()
    }
  }, [doLogin, mixpanelInitialized, isAuthenticating, oktaBaseUrl, oktaClientId, isLoginFromPartnerSSO, oktaAuth])

  // Call mixpanel track, set alias and register super props
  useEffect(() => {
    const localStoragePartnerIdp = window.localStorage.getItem(PARTNER_SSO_REGISTRATION_PARTNER_IDP_QUERY_PARAM)

    // If partner IDP name was stored in local storage, it's because we redirected and the initially set login event was lost.
    // In this case, we need to set the login event to LOGIN_PARTNER_SSO and remove the partner IDP name from local storage.
    if (!hasTrackedLogin && !!userId && !!customerId && localStoragePartnerIdp && !loginEvent) {
      window.localStorage.removeItem(PARTNER_SSO_REGISTRATION_PARTNER_IDP_QUERY_PARAM)
      setLoginEvent(LOGIN_EVENTS.LOGIN_PARTNER_SSO)
      setLoginEventProps({ [COMMON_MIXPANEL_PROPERTIES.PARTNER_NAME]: localStoragePartnerIdp })
    }

    if (!hasTrackedLogin && !!userId && !!customerId && !!loginEvent && mixpanelInitialized) {
      mixpanel.register({
        lyra_id: userId,
        customer_id: customerId,
      })
      mixpanel.alias(userId)
      trackEventWithObj({
        page: COMMON_MIXPANEL_PAGE.LOGIN,
        event: loginEvent,
        ...loginEventProps,
      })

      setHasTrackedLogin(true)
      const showProviderResultsSurveyUsingDateLastSeen = getShowProviderResultsSurveyUsingDateLastSeen({
        providerResultsSurveyDateLastSeen:
          localStorage.getItem(PROVIDER_RESULTS_SURVEY_DATE_LAST_SEEN_KEY) ?? undefined,
      })
      if (showProviderResultsSurveyUsingDateLastSeen) {
        localStorage.setItem(SHOW_PROVIDER_RESULTS_SURVEY_KEY, 'true')
      } else {
        localStorage.setItem(SHOW_PROVIDER_RESULTS_SURVEY_KEY, 'false')
      }
    }
  }, [customerId, hasTrackedLogin, loginEvent, loginEventProps, trackEventWithObj, userId, mixpanelInitialized])

  return isAuthenticating
}

export default useAuthenticate
